import styles from './one.module.css'
import { Chat } from '../../../components/Chat'
import { PillButton } from '../../../components/PillButton'
import { ReactComponent as UploadFile } from '../../../assets/DownloadFileLine.svg'
import { Layout } from '../../../components/Layout'
import { useCallback, useState } from 'react'



const hardcodedText = `# Optimization Problem for Starbucks Coffee Supply Chain

The optimization problem for Starbucks involves managing a complex supply chain that includes coffee farmers, roasting facilities, distribution centers, and retail stores. The goal is to maximize profitability and customer satisfaction while maintaining quality and sustainability. Below is a structured approach to formulate this optimization problem.

## Decision Variables

- \\( x_{f,r} \\): Quantity of coffee sourced from farmer \\( f \\) to roasting facility \\( r \\).
- \\( y_{r,d} \\): Quantity of roasted coffee shipped from roasting facility \\( r \\) to distribution center \\( d \\).
- \\( z_{d,s} \\): Quantity of coffee shipped from distribution center \\( d \\) to retail store \\( s \\).
- \\( w_{r} \\): Binary variable indicating whether roasting facility \\( r \\) is operational.
- \\( v_{s} \\): Binary variable indicating whether retail store \\( s \\) is operational.

## Parameters

- \\( C_{f} \\): Cost of sourcing coffee from farmer \\( f \\).
- \\( C_{r} \\): Roasting cost per unit at roasting facility \\( r \\).
- \\( C_{d} \\): Shipping cost per unit from distribution center \\( d \\) to retail store \\( s \\).
- \\( D_{s} \\): Demand for coffee at retail store \\( s \\).
- \\( Q_{f} \\): Quality score of coffee from farmer \\( f \\).
- \\( Q_{r} \\): Quality requirements at roasting facility \\( r \\).
- \\( C_{max} \\): Maximum capacity of roasting facility \\( r \\).
- \\( S_{f} \\): Supply capacity of farmer \\( f \\).
- \\( S_{d} \\): Capacity of distribution center \\( d \\).
- \\( P_{s} \\): Profit per unit sold at retail store \\( s \\).

## Constraints

### Supply Capacity Constraints
Ensure that the amount sourced from each farmer does not exceed their supply capacity.

\\[
\\sum_{r} x_{f,r} \\leq S_{f}, \\quad \\forall f
\\]

### Roasting Capacity Constraints
Ensure that the amount roasted at each facility does not exceed its capacity.

\\[
\\sum_{d} y_{r,d} \\leq C_{max} \\cdot w_{r}, \\quad \\forall r
\\]

### Distribution Capacity Constraints
Ensure that the amount shipped from each distribution center does not exceed its capacity.

\\[
\\sum_{s} z_{d,s} \\leq S_{d}, \\quad \\forall d
\\]

### Demand Fulfillment Constraints
Ensure that the demand at each retail store is met.

\\[
\\sum_{d} z_{d,s} \\geq D_{s}, \\quad \\forall s
\\]

### Quality Constraints
Ensure that the quality of coffee meets the required standards at each roasting facility.

\\[
Q_{f} \\geq Q_{r}, \\quad \\forall f, r \\text{ where } x_{f,r} > 0
\\]

### Non-negativity and Binary Constraints
All decision variables must be non-negative, and binary variables must be either 0 or 1.

\\[
x_{f,r} \\geq 0, \\quad y_{r,d} \\geq 0, \\quad z_{d,s} \\geq 0
\\]
\\[
w_{r} \\in \\{0, 1\\}, \\quad v_{s} \\in \\{0, 1\\}
\\]

## Objective Function

**Maximize** the total profit, which is the revenue from retail sales minus the costs of sourcing, roasting, and shipping.

\\[
\\text{Maximize} \\quad \\sum_{s} P_{s} \\cdot D_{s} - \\sum_{f,r} C_{f} \\cdot x_{f,r} - \\sum_{r} C_{r} \\cdot y_{r,d} - \\sum_{d,s} C_{d} \\cdot z_{d,s}
\\]

## JSON Representation

\`\`\`json
{
  "problem_description": "Optimize Starbucks' global coffee supply chain to maximize profitability and customer satisfaction while maintaining quality and sustainability.",
  "variables": [
    {
      "name": "x_{f,r}",
      "definition": "Quantity of coffee sourced from farmer f to roasting facility r",
      "type": "integer"
    },
    {
      "name": "y_{r,d}",
      "definition": "Quantity of roasted coffee shipped from roasting facility r to distribution center d",
      "type": "integer"
    },
    {
      "name": "z_{d,s}",
      "definition": "Quantity of coffee shipped from distribution center d to retail store s",
      "type": "integer"
    },
    {
      "name": "w_{r}",
      "definition": "Binary variable indicating whether roasting facility r is operational",
      "type": "binary"
    },
    {
      "name": "v_{s}",
      "definition": "Binary variable indicating whether retail store s is operational",
      "type": "binary"
    }
  ],
  "parameters": [
    {
      "name": "C_{f}",
      "definition": "Cost of sourcing coffee from farmer f"
    },
    {
      "name": "C_{r}",
      "definition": "Roasting cost per unit at roasting facility r"
    },
    {
      "name": "C_{d}",
      "definition": "Shipping cost per unit from distribution center d to retail store s"
    },
    {
      "name": "D_{s}",
      "definition": "Demand for coffee at retail store s"
    },
    {
      "name": "Q_{f}",
      "definition": "Quality score of coffee from farmer f"
    },
    {
      "name": "Q_{r}",
      "definition": "Quality requirements at roasting facility r"
    },
    {
      "name": "C_{max}",
      "definition": "Maximum capacity of roasting facility r"
    },
    {
      "name": "S_{f}",
      "definition": "Supply capacity of farmer f"
    },
    {
      "name": "S_{d}",
      "definition": "Capacity of distribution center d"
    },
    {
      "name": "P_{s}",
      "definition": "Profit per unit sold at retail store s"
    }
  ],
  "constraints": [
    {
      "description": "Supply capacity constraints",
      "formulation": "\\\\sum_{r} x_{f,r} \\\\leq S_{f}, \\\\quad \\\\forall f"
    },
    {
      "description": "Roasting capacity constraints",
      "formulation": "\\\\sum_{d} y_{r,d} \\\\leq C_{max} \\\\cdot w_{r}, \\\\quad \\\\forall r"
    },
    {
      "description": "Distribution capacity constraints",
      "formulation": "\\\\sum_{s} z_{d,s} \\\\leq S_{d}, \\\\quad \\\\forall d"
    },
    {
      "description": "Demand fulfillment constraints",
      "formulation": "\\\\sum_{d} z_{d,s} \\\\geq D_{s}, \\\\quad \\\\forall s"
    },
    {
      "description": "Quality constraints",
      "formulation": "Q_{f} \\\\geq Q_{r}, \\\\quad \\\\forall f, r \\\\text{ where } x_{f,r} > 0"
    },
    {
      "description": "Non-negativity and binary constraints",
      "formulation": "x_{f,r} \\\\geq 0, \\\\quad y_{r,d} \\\\geq 0, \\\\quad z_{d,s} \\\\geq 0"
    }
  ],
  "objective_function": {
    "description": "Maximize total profit",
    "formulation": "\\\\text{Maximize} \\\\quad \\\\sum_{s} P_{s} \\\\cdot D_{s} - \\\\sum_{f,r} C_{f} \\\\cdot x_{f,r} - \\\\sum_{r} C_{r} \\\\cdot y_{r,d} - \\\\sum_{d,s} C_{d} \\\\cdot z_{d,s}"
  }
}
\`\`\`

## Complete MILP Formulation in LaTeX

\`\`\`latex
\\begin{align*}
\\text{Maximize} & \\quad \\sum_{s} P_{s} \\cdot D_{s} - \\sum_{f,r} C_{f} \\cdot x_{f,r} - \\sum_{r} C_{r} \\cdot y_{r,d} - \\sum_{d,s} C_{d} \\cdot z_{d,s} \\\\
\\text{Subject to} & \\quad \\sum_{r} x_{f,r} \\leq S_{f}, \\quad \\forall f \\\\
& \\quad \\sum_{d} y_{r,d} \\leq C_{max} \\cdot w_{r}, \\quad \\forall r \\\\
& \\quad \\sum_{s} z_{d,s} \\leq S_{d}, \\quad \\forall d \\\\
& \\quad \\sum_{d} z_{d,s} \\geq D_{s}, \\quad \\forall s \\\\
& \\quad Q_{f} \\geq Q_{r}, \\quad \\forall f, r \\text{ where } x_{f,r} > 0 \\\\
& \\quad x_{f,r} \\geq 0, \\quad y_{r,d} \\geq 0, \\quad z_{d,s} \\geq 0 \\\\
& \\quad w_{r} \\in \\{0, 1\\}, \\quad v_{s} \\in \\{0, 1\\}
\\end{align*}
\`\`\`

This formulation captures the complexity of Starbucks' supply chain, ensuring that all constraints are met while maximizing profitability and customer satisfaction.`;

interface StepOneProps {
  onNext: (data: string) => void;
  isLoading?: boolean;
  processingMessage?: string;
}



const StepOne: React.FC<StepOneProps> = ({ onNext, isLoading = false, processingMessage = '' }) => {
  const [chat, setChat] = useState<string>('');

  return (
    <Layout>
      <div className={styles.content}>
        <div className={styles.builderContent}>
          <p className={styles.stepIndicator}>Step 1 of 4</p>
          <div className={styles.formContainer}>
            <div className={styles.titleSection}>
              <p className={styles.title}>Tell us about your supply chain</p>
              <p className={styles.subtitle}>This information will help our model adjust specifically to your supply chain demands.</p>
            </div>
            <div className={styles.questionSection}>
              <p className={styles.questionLabel}>How does your supply chain work?</p>
              <Chat onSubmit={(e) => { console.log(e) }} onChange={(e) => { setChat(e) }} placeholder="My business works like this: ..." >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0px 16px 12px 16px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '500', color: '#777777' }}>Or upload a document</p>
                  <div>
                    <PillButton variant="secondary" label=".docx or .pdf" prefix={<UploadFile />} />
                  </div>
                </div>
              </Chat>

              {processingMessage && (
                <div className={styles.processingContainer}>
                  <div className={styles.spinner}></div>
                  <p className={styles.processingMessage}>{processingMessage}</p>
                </div>
              )}

              <button
                className={styles.continueButton}
                onClick={() => {
                  onNext(chat.length >= 200 ? chat : hardcodedText);
                }}
                disabled={isLoading || chat.length < 200}
              >
                {isLoading && !processingMessage ? 'Processing...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StepOne; 